import { clearStoredToken, ensureToken, getStoredToken } from "./auth";

/**
 * Enhanced fetch wrapper for the Portal API.
 * 
 * Automatically handles:
 * - JSON headers (Accept/Content-Type)
 * - X-Requested-With header
 * - CSRF token for state-changing requests
 * - Sanctum Bearer token from in-memory storage
 * - Session cookies (same-origin)
 * - 401 Unauthorized handling (token refresh attempt)
 */
export async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  // Guard: prevent fetch with null/empty URL (causes "admin/null" style errors)
  if (!url || typeof url !== 'string') {
    throw new Error('apiFetch: URL is required')
  }
  const token = getStoredToken();
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  const defaultHeaders: Record<string, string> = {
    "Accept": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };

  if (token) {
    defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  // Add CSRF token for non-GET requests if session is being used alongside Sanctum
  if (options.method && options.method !== "GET" && csrfToken) {
    defaultHeaders["X-CSRF-TOKEN"] = csrfToken;
  }

  // Ensure Content-Type is set for requests with body if not already set.
  // Do NOT set Content-Type for FormData—the browser must set multipart/form-data with boundary.
  if (options.body && !(options.body instanceof FormData) && !options.headers?.["Content-Type"]) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  const mergedOptions: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: "same-origin", // Always send session cookies too
  };

  const response = await fetch(url, mergedOptions)

  // Handle 401 Unauthorized
  if (response.status === 401) {
    // If the token was invalid, clear it and try to get a new one once
    clearStoredToken();

    // Attempt to get a new token from session
    const newToken = await ensureToken();

    if (newToken) {
      // Retry the request once with the new token
      return apiFetch<T>(url, {
        ...options,
        headers: {
          ...options.headers,
          "Authorization": `Bearer ${newToken}`,
        }
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  // Handle different response types
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  return response as unknown as T;
}
